/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Skeleton } from "primereact/skeleton";
import {
  Boton,
  Titulo,
  Formulario,
  accessAPI,
  Sidebar,
  formatFecha,
  tituloBase,
  TablaFiltrable
} from "../../shared";

// Acreditaciones del organismo certificador (por ejemplo ante OUA). El
// alcance de cada una es el conjunto de normas vinculadas; se gestiona desde
// la ficha de cada acreditación.
export default function Acreditaciones() {
  const [loader, setLoader] = useState(true);
  const [acreditaciones, setAcreditaciones] = useState([]);
  const [sideBar, setSideBar] = useState(false);
  const [formLoader, setFormLoader] = useState(false);
  const [formKey, setFormKey] = useState(0);

  const toast = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Acreditaciones";
    accessAPI(
      "GET",
      "admin/acreditacion",
      null,
      (respuesta) => {
        setAcreditaciones(respuesta);
        setLoader(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar acreditaciones",
          sticky: true
        });
        setLoader(false);
      }
    );
    return () => (document.title = tituloBase());
  }, []);

  function crearAcreditacion(contenidoCampos) {
    setFormLoader(true);
    accessAPI(
      "POST",
      "admin/acreditacion",
      contenidoCampos,
      (respuesta) => {
        navigate(`/acreditacion/${respuesta.id}`);
      },
      (respuesta) => {
        setFormLoader(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al crear la acreditación",
          sticky: true
        });
      }
    );
  }

  const formConfig = {
    secciones: [
      {
        campos: [
          {
            nombre: "organismoAcreditacion",
            tipoitemformularioid: 1,
            etiqueta: "Organismo de acreditación",
            requerido: true
          },
          {
            nombre: "pais",
            tipoitemformularioid: 1,
            etiqueta: "País del organismo"
          },
          {
            nombre: "fechaEmision",
            tipoitemformularioid: 7,
            etiqueta: "Fecha de emisión"
          },
          {
            nombre: "fechaValidez",
            tipoitemformularioid: 7,
            etiqueta: "Fecha de validez"
          },
          {
            nombre: "linkAlcance",
            tipoitemformularioid: 1,
            etiqueta: "Link al alcance"
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Crear acreditación",
        callback: crearAcreditacion,
        validar: true
      }
    ]
  };

  const activas = acreditaciones.filter((a) => !a.suspendida);
  const suspendidas = acreditaciones.filter((a) => a.suspendida);

  // Fecha del último evento de suspensión registrado en el historial
  function fechaSuspension(acreditacion) {
    const historial = Array.isArray(acreditacion.historialSuspension)
      ? acreditacion.historialSuspension
      : [];
    const ultima = [...historial]
      .reverse()
      .find((e) => e.evento === "SUSPENSION");
    return ultima ? formatFecha(ultima.fecha) : "—";
  }

  function abrirAcreditacion(e) {
    if (e.originalEvent.metaKey || e.originalEvent.ctrlKey) {
      window.open(`/acreditacion/${e.data.id}`, "_blank");
    } else {
      navigate(`/acreditacion/${e.data.id}`);
    }
  }

  return (
    <>
      <Toast ref={toast} position="top-center" />

      <Sidebar
        visible={sideBar}
        onHide={() => setSideBar(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Nueva acreditación</Titulo>
        <Formulario key={formKey} config={formConfig} loader={formLoader} />
      </Sidebar>

      {loader && (
        <div
          css={{
            marginTop: "2em",
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem"
          }}
        >
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "0.5rem"
            }}
          >
            <Skeleton width="15rem" height="2.5rem" />
            <Skeleton width="12rem" height="2.5rem" />
          </div>
          <Card>
            <div
              css={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem"
              }}
            >
              <Skeleton width="100%" height="3rem" />
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} width="100%" height="3.5rem" />
              ))}
            </div>
          </Card>
        </div>
      )}

      {!loader && (
        <div>
          <Titulo
            botones={
              <Boton
                callback={() => {
                  setFormKey((k) => k + 1);
                  setSideBar(true);
                }}
              >
                Nueva acreditación
              </Boton>
            }
          >
            Acreditaciones
          </Titulo>

          <Card>
            <TablaFiltrable conFiltros={false}
              value={activas}
              emptyMessage="No hay acreditaciones registradas"
              stripedRows
              size="small"
              onRowClick={abrirAcreditacion}
              css={{ "& .p-datatable-tbody > tr": { cursor: "pointer" } }}
              nombreArchivo="acreditaciones"
            >
              <Column header="Organismo" field="organismoAcreditacion" />
              <Column header="País" body={(rowData) => rowData.pais || "—"} />
              <Column
                header="Emisión"
                body={(rowData) =>
                  rowData.fechaEmision ? formatFecha(rowData.fechaEmision) : "—"
                }
                style={{ width: "9em" }}
              />
              <Column
                header="Validez"
                body={(rowData) =>
                  rowData.fechaValidez ? formatFecha(rowData.fechaValidez) : "—"
                }
                style={{ width: "9em" }}
              />
              <Column
                header="Normas"
                field="cantidadNormas"
                style={{ width: "7em", textAlign: "center" }}
              />
            </TablaFiltrable>
          </Card>

          {/* Las acreditaciones no se eliminan: las suspendidas van aparte */}
          {suspendidas.length > 0 && (
            <Card css={{ marginTop: "1em" }}>
              <Titulo>
                <i
                  className="pi pi-pause"
                  css={{ marginRight: "0.5em" }}
                ></i>
                Acreditaciones suspendidas
              </Titulo>
              <TablaFiltrable conFiltros={false}
                value={suspendidas}
                size="small"
                onRowClick={abrirAcreditacion}
                css={{ "& .p-datatable-tbody > tr": { cursor: "pointer" } }}
                nombreArchivo="acreditaciones_suspendidas"
              >
                <Column header="Organismo" field="organismoAcreditacion" />
                <Column
                  header="País"
                  body={(rowData) => rowData.pais || "—"}
                />
                <Column
                  header="Fecha de suspensión"
                  body={fechaSuspension}
                  style={{ width: "12em" }}
                />
                <Column
                  header="Normas"
                  field="cantidadNormas"
                  style={{ width: "7em", textAlign: "center" }}
                />
              </TablaFiltrable>
            </Card>
          )}
        </div>
      )}
    </>
  );
}
